// Markets · 6-tile asset-class grid with live sparklines
// (extracted from HeroArtwork.jsx; behaviour preserved)
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Coins, Gem, BarChart3, Bitcoin, Fuel, Building2 } from "lucide-react";
import { glassCard, Sparkline } from "./_shared";

const ASSET_TILES = [
  { Icon: Coins,      label: "FX",       count: "60+",  spark: [4, 6, 5, 8, 9, 11, 10, 13], color: "#62cfff" },
  { Icon: Gem,        label: "METALS",   count: "8",    spark: [3, 5, 4, 7, 8, 10, 12, 14], color: "#e9c46a" },
  { Icon: BarChart3,  label: "INDICES",  count: "30+",  spark: [5, 7, 6, 9, 11, 10, 13, 15], color: "#7be08c" },
  { Icon: Bitcoin,    label: "CRYPTO",   count: "50+",  spark: [6, 9, 7, 11, 13, 10, 14, 16], color: "#f7931a" },
  { Icon: Fuel,       label: "ENERGY",   count: "4",    spark: [4, 5, 7, 6, 8, 10, 9, 11],  color: "#ff9466" },
  { Icon: Building2,  label: "SHARES",   count: "1000+",spark: [5, 7, 6, 8, 10, 12, 11, 14], color: "#c79bff" },
];

// Per-tile metadata: a baseline live "price", tick size and digits so
// each asset class shows a realistic looking quote rather than just a
// generic pct number. This is what makes the slide feel like a live
// terminal — every tile carries a hero number that ticks every second.
const TILE_LIVE = {
  FX:      { base: 1.16370,  step: 0.00012, digits: 5, prefix: "" },
  METALS:  { base: 2341.20,  step: 0.45,    digits: 2, prefix: "$" },
  INDICES: { base: 19842.5,  step: 4.5,     digits: 1, prefix: "" },
  CRYPTO:  { base: 68420,    step: 18,      digits: 0, prefix: "$" },
  ENERGY:  { base: 73.42,    step: 0.04,    digits: 2, prefix: "$" },
  SHARES:  { base: 248.90,   step: 0.18,    digits: 2, prefix: "$" },
};

/* Animated mini tile · live-feel ticker that gives each asset class
   tile a constantly moving price + pct badge + walking sparkline so
   the markets slide reads as a real multi-asset terminal. `featured`
   flips true when this tile is the current "spotlight" in the parent
   carousel — it then scales up, brightens its border and lifts its
   shadow, drawing the eye to one tile at a time. */
function MarketTile({ tile, index, accent, featured }) {
  // Different drift period per tile so they don't all flash together.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // 1.6 s baseline · slower flashes so the eye can actually settle
    // on each price update. Per-tile jitter desyncs them.
    const id = setInterval(() => setTick((t) => t + 1), 1600 + (index % 4) * 240);
    return () => clearInterval(id);
  }, [index]);

  // Live price walker · deterministic sine + small random jitter so
  // numbers feel organic but stable across renders within a tick.
  const live = TILE_LIVE[tile.label] || TILE_LIVE.FX;
  const price = useMemo(() => {
    const phase = tick * 0.32 + index * 1.3;
    const drift = Math.sin(phase) * live.step * 5 + Math.cos(phase * 1.4) * live.step * 2;
    return live.base + drift;
  }, [tick, index, live]);

  // Walking sparkline · the chart shifts left each tick like a real ticker.
  const series = useMemo(() => {
    const out = tile.spark.slice();
    for (let i = 0; i < tick % 24; i++) {
      out.shift();
      const last = out[out.length - 1] ?? 8;
      const drift = Math.sin(tick * 0.55 + index * 0.9) * 2 + (Math.random() * 1.2 - 0.6);
      out.push(Math.max(2, Math.min(18, last + drift)));
    }
    return out;
  }, [tick, tile.spark, index]);

  // Bigger pct swings (±0.8%) so the badge is clearly changing.
  const pct = useMemo(
    () => (Math.sin(tick * 0.42 + index * 1.7) * 0.82).toFixed(2),
    [tick, index]
  );
  const up = parseFloat(pct) >= 0;

  return (
    <motion.div
      className="relative rounded-xl p-3 flex flex-col justify-between overflow-hidden"
      style={glassCard(tile.color, featured ? 100 : 85)}
      initial={{ opacity: 0, y: 8 }}
      // Spotlight transform · the featured tile scales up, lifts, and
      // gets a brighter border so it grabs the eye. Non-featured tiles
      // recede a hair so the contrast reads.
      animate={
        featured
          ? {
              opacity: 1,
              y: -4,
              scale: 1.07,
              boxShadow: `0 18px 40px -12px color-mix(in srgb, ${tile.color} 55%, transparent)`,
              borderColor: `color-mix(in srgb, ${tile.color} 80%, transparent)`,
            }
          : {
              opacity: 0.78,
              y: 0,
              scale: 1,
              boxShadow: "none",
              borderColor: "rgba(255,255,255,0.06)",
            }
      }
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Slow flash overlay · brief brand-tinted pulse on each tick.
          Shorter than 1s so the colour cue is felt, not a strobe. */}
      <motion.div
        key={`flash-${tick}`}
        aria-hidden
        className="pointer-events-none absolute inset-0"
        initial={{ background: `color-mix(in srgb, ${up ? "#54d97c" : "#f06a6a"} 16%, transparent)`, opacity: 0.55 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
      />

      <div className="flex items-center justify-between relative">
        <div className="w-7 h-7 rounded-md grid place-items-center"
             style={{
               background: `color-mix(in srgb, ${tile.color} 12%, transparent)`,
               border: `1px solid color-mix(in srgb, ${tile.color} 40%, transparent)`,
             }}>
          <tile.Icon size={14} strokeWidth={1.7} style={{ color: tile.color }} />
        </div>
        <motion.div
          key={`badge-${tick}`}
          initial={{ scale: 0.85, opacity: 0.4 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.45 }}
          className="font-mono tabular-nums px-1.5 py-0.5 rounded"
          style={{
            fontSize: 9,
            color: up ? "#0a7a2f" : "#9b2c2c",
            background: up ? "rgba(80,220,130,0.20)" : "rgba(240,106,106,0.20)",
            letterSpacing: 0.2,
          }}
        >
          {up ? "+" : ""}{pct}%
        </motion.div>
      </div>

      <div className="font-mono font-bold uppercase tracking-wide mt-1 relative"
           style={{ fontSize: 9, color: "var(--text-1)" }}>
        {tile.label}
      </div>

      {/* Live price · headline number, swaps in with a soft 0.45 s
          fade-up · slower than before to match the calmer tick rate. */}
      <motion.div
        key={`price-${tick}`}
        initial={{ y: -2, opacity: 0.55 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="font-mono tabular-nums font-semibold leading-none relative mt-1"
        style={{ fontSize: 13, color: "var(--text-1)" }}
      >
        {live.prefix}{price.toFixed(live.digits)}
      </motion.div>

      <div className="flex items-end justify-between mt-1.5 relative">
        <div>
          <div className="font-display font-bold leading-none" style={{ fontSize: 16, color: "var(--text-1)" }}>
            {tile.count}
          </div>
          <div className="font-mono uppercase mt-0.5" style={{ fontSize: 7, color: "var(--text-3)", letterSpacing: "0.16em" }}>
            Instruments
          </div>
        </div>
        <Sparkline data={series} color={tile.color} w={46} h={20} />
      </div>
    </motion.div>
  );
}

function MarketsArt({ accent }) {
  // Spotlight carousel · cycles through the 6 asset-class tiles every
  // 2.8 s so each one takes a turn being big & centre-of-attention.
  // Using a top-level counter keeps every tile's animation in sync —
  // there's only ever one featured tile at a time.
  const [featured, setFeatured] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setFeatured((i) => (i + 1) % ASSET_TILES.length),
      2800
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="relative w-full aspect-square max-w-[460px] mx-auto overflow-hidden"
      data-testid="hero-art-markets"
      style={{ perspective: "1400px" }}
    >
      {/* Backdrop halo */}
      <div
        aria-hidden
        className="absolute inset-[18%] rounded-3xl pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 50%, color-mix(in srgb, ${accent} 12%, transparent) 0%, transparent 65%)`,
          filter: "blur(16px)",
        }}
      />

      {/* Scrolling ticker tape behind the grid · adds an ambient sense
          of motion. Uses GPU transform so it doesn't hurt scroll perf. */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-[46%] h-7 overflow-hidden pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, color-mix(in srgb, ${accent} 8%, transparent), transparent)`,
        }}
      >
        <motion.div
          className="absolute inset-y-0 flex items-center gap-6 whitespace-nowrap font-mono tabular-nums"
          style={{ fontSize: 9, color: "var(--text-3)", letterSpacing: 0.2 }}
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        >
          {Array.from({ length: 2 }).flatMap((_, dup) => [
            <span key={`a-${dup}`}>EURUSD <span style={{color:"#0a7a2f"}}>1.16370 ▲</span></span>,
            <span key={`b-${dup}`}>XAUUSD <span style={{color:"#0a7a2f"}}>2,341.20 ▲</span></span>,
            <span key={`c-${dup}`}>NAS100 <span style={{color:"#9b2c2c"}}>19,842.5 ▼</span></span>,
            <span key={`d-${dup}`}>BTCUSD <span style={{color:"#0a7a2f"}}>68,420 ▲</span></span>,
            <span key={`e-${dup}`}>GBPJPY <span style={{color:"#9b2c2c"}}>192.380 ▼</span></span>,
            <span key={`f-${dup}`}>USOIL  <span style={{color:"#0a7a2f"}}>73.42 ▲</span></span>,
            <span key={`g-${dup}`}>TSLA   <span style={{color:"#0a7a2f"}}>248.90 ▲</span></span>,
          ])}
        </motion.div>
      </div>

      {/* 3D-tilted grid wrapper */}
      <motion.div
        className="absolute inset-[6%] grid grid-cols-2 grid-rows-3 gap-3"
        style={{
          transform: "rotateY(-10deg) rotateX(6deg)",
          transformStyle: "preserve-3d",
        }}
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      >
        {ASSET_TILES.map((tile, i) => (
          <MarketTile
            key={tile.label}
            tile={tile}
            index={i}
            accent={accent}
            featured={i === featured}
          />
        ))}
      </motion.div>
    </div>
  );
}


export default MarketsArt;
